import { useSyncExternalStore } from "react";

const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((l) => l());
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  const onStorage = () => cb();
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(cb);
    window.removeEventListener("storage", onStorage);
  };
}

export function readStorage(key: string): string {
  try {
    return sessionStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

/** True when the key exists at all — including when its value is "". */
export function hasStorage(key: string): boolean {
  try {
    return sessionStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

export function writeStorage(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value || "");
  } catch {
    /* ignore */
  }
  notify();
}

export function removeStorage(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
  notify();
}

/** React hook that re-renders when the given sessionStorage key changes. */
export function useStorage(key: string): [string, (v: string) => void] {
  const value = useSyncExternalStore(
    subscribe,
    () => readStorage(key),
    () => "",
  );
  return [value, (v: string) => writeStorage(key, v)];
}

export const STORAGE_KEYS = {
  address: "conny_address_raw",
  street: "conny_address_street",
  house: "conny_address_house",
  zip: "conny_zip_code",
  city: "conny_address_city",
  rent: "conny_current_rent",
  area: "conny_apartment_living_area",
  landlordCategory: "conny_landlord_category",
  landlordKnownKey: "conny_landlord_known_key",
  landlordRaw: "conny_landlord_raw",
  landlordType: "conny_landlord_type",
  landlordUserSelected: "conny_landlord_user_selected",
  landlordAutoSelected: "conny_landlord_auto_selected",
  landlordSelectedForAddress: "conny_landlord_selected_for_address",
  apiBase: "conny_api_base",
  apiToken: "conny_api_token",
  mapTileKey: "conny_map_tile_key",
  apiShowConfig: "conny_api_show_config",
  locale: "conny_locale",
  moveInDate: "conny_move_in_date",
} as const;
